import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BESOINS = [
  "Raccordement fibre optique FTTO",
  "Raccordement FTTH (segment D3)",
  "Sous-traitance / partenariat opérateur",
  "Travaux / dépose réseaux cuivre",
  "Inspection / contrôle de conformité",
  "Audit qualité / suivi QHSE",
  "Autre demande",
];

const initialForm = {
  nom: "",
  societe: "",
  email: "",
  telephone: "",
  besoin: "",
  message: "",
};

const DevisForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.besoin) {
      toast.error("Veuillez sélectionner votre type de besoin.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/devis`, form);
      toast.success("Votre demande de devis a bien été envoyée. Nous revenons vers vous rapidement.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Une erreur est survenue. Vous pouvez aussi nous appeler au 06 05 76 50 66.");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "rounded-none border-line bg-white focus-visible:ring-2 focus-visible:ring-midnight/20 focus-visible:ring-offset-0";

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="devis-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="devis-nom">Nom et prénom *</Label>
          <Input
            id="devis-nom"
            data-testid="devis-nom-input"
            required
            minLength={2}
            value={form.nom}
            onChange={set("nom")}
            placeholder="Votre nom"
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="devis-societe">Société</Label>
          <Input
            id="devis-societe"
            data-testid="devis-societe-input"
            value={form.societe}
            onChange={set("societe")}
            placeholder="Nom de votre organisation"
            className={fieldClass}
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="devis-email">E-mail professionnel *</Label>
          <Input
            id="devis-email"
            type="email"
            data-testid="devis-email-input"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="vous@entreprise.fr"
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="devis-telephone">Téléphone *</Label>
          <Input
            id="devis-telephone"
            type="tel"
            data-testid="devis-telephone-input"
            required
            minLength={8}
            value={form.telephone}
            onChange={set("telephone")}
            placeholder="06 00 00 00 00"
            className={fieldClass}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Type de besoin *</Label>
        <Select value={form.besoin} onValueChange={(v) => setForm({ ...form, besoin: v })}>
          <SelectTrigger className="rounded-none border-line" data-testid="devis-besoin-select">
            <SelectValue placeholder="Sélectionnez votre besoin" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {BESOINS.map((b) => (
              <SelectItem key={b} value={b} data-testid={`devis-besoin-${b.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}>
                {b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="devis-message">Votre projet *</Label>
        <Textarea
          id="devis-message"
          data-testid="devis-message-input"
          required
          minLength={10}
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="Décrivez votre besoin : type de site, localisation, volumétrie, délais souhaités…"
          className={fieldClass}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="devis-submit-button"
        className="group flex w-full items-center justify-center gap-2 bg-midnight px-6 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-ciel hover:text-midnight disabled:opacity-60 sm:w-auto"
      >
        {loading ? "Envoi en cours…" : "Envoyer ma demande"}
        <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
      </button>
      <p className="text-xs text-steel">
        Vos informations servent uniquement à traiter votre demande.
      </p>
    </form>
  );
};

export default DevisForm;
